import { t } from '@lingui/macro';
import { FacilityCertificate, ProcessingOptionType } from 'features/suppliers/types';

// MappingStatus enum - only selectable options (PartiallyIncluded is display-only)
export enum MappingStatus {
  Include = 0,
  Exclude = 1,
}

export interface StyleData {
  id: number;
  styleName?: string;
  styleNumber?: string;
  season?: Season;
  brand?: Brand;
  brandContactPerson?: string;
  project?: null | string;
  state?: number;
  group?: Group;
  categories?: string;
  description?: string;
  styleLogo?: string;
  isPrimaryStyle?: boolean;
  styleColors?: StyleColor[];
  styleColorName?: null | string;
  styleColorID?: null | string;
  styleColorID2?: string | null;
  customFields?: CustomField[];
  // for row selection styleId and styleColorId
  styleId?: number;
  styleColorId?: number;
  mappingStatus?: string;
  poVariantId?: number;
  deletedAt?: string | null;
  organization?: {
    name?: string;
  };
  hex?: string;
  sizes?: string;
  // for hierarchical table structure
  childs?: StyleData[];
  subsupplierFacilityDetailsList?: PoProcessType[];
  productCertificateDetailsList?: PoProcessType[];
  styleChildId?: string;
}

export interface Season {
  id?: number;
  name?: string;
}

export interface Group {
  id?: number;
  name?: string;
}

export interface Brand {
  id?: number;
  name?: string;
}

export interface CustomField {
  value?: string;
  type?: number;
  id?: number;
  position?: number;
  name?: string;
}

export interface StyleColor {
  colorId1?: string;
  colorId2?: string | null;
  id?: number;
  name?: string;
  mappingStatus?: string;
  poVariantId?: number; // POVariant ID for this specific style color
  poFacilityProcessId?: number;
  hex?: string;
  sizes?: string;
}

export const customFieldTypeToColumnType: Record<number, string> = {
  0: 'dd', //0 `Allowed Values`
  1: `date`, //1 `Date`
  2: 'text', //2 `Text`
  3: 'dd', //3 t`Hierarchy Values`
  4: 'number', //4 `Numeric`
  5: 'divider',
};

export const customFieldType: Record<number, string> = {
  0: 'allowedvalues', //0 `Allowed Values`
  1: `date`, //1 `Date`
  2: 'text', //2 `Text`
  3: 'nestedallowedvalues', //3 t`Hierarchy Values`
  4: 'number', //4 `Numeric`
  5: 'divider',
};

export const styleState: Record<number, string> = {
  0: t`Unpublished`,
  1: t`Published`,
  2: t`Deleted`,
  3: t`Delivered`,
  4: t`Cancelled`,
  5: t`Work In Progress`,
};

/** Display label when a supplier user views a subsupplier mapped by the designer. */
export const MAPPED_BY_DESIGNER = 'Mapped by designer';

export interface CustomFieldFilterOption {
  customFieldForFilter?: CustomFieldForFilter;
  filterFieldID?: number;
  filterFieldName?: string;
  options?: Option[];
  children?: CustomFieldFilterOption[];
}

interface CustomFieldForFilter {
  customFieldName?: string;
  customFieldId?: number;
  customFieldType?: number;
  position?: number;
}
interface Option {
  id?: string;
  value?: string;
  displayName?: string;
  type?: number;
  isArchived?: boolean;
}

export interface ItemData {
  id: number;
  poItemSettingId?: number; // POItemSetting primary key for updates
  itemName?: string;
  itemNumber?: string;
  status?: string;
  isMainItem?: boolean;
  itemComposition?: string;
  itemWeight?: string;
  itemWeightUnit?: string;
  itemSupplier?: string;
  categories?: string;
  linkedStyles?: LinkedStyle[];
  mappingStatus?: string;
  suppliedBy?: string;
  itemBrand?: string;
  itemBrandLogoUrl?: string;
  subsupplierFacilityDetailsList?: PoProcessType[];
  itemLogo?: string;
  description?: string;
  brand?: Brand;
  brandContactPerson?: string;
}

export interface LinkedStyle {
  id?: number;
  styleName: string;
  styleNumber?: string;
  season?: Group;
  project?: string;
  state?: number;
  group?: Group;
}

// Purchase Order Creation Types
export type ProcessTypeSelectionItem = {
  poObjectiveId: number;
  processTypeId: number;
  facilityProcessCategoryId: number;
  isMandatory: boolean;
};

export interface PoStyleVariant {
  styleId: number;
  styleColorId?: number;
}

export interface CreatePurchaseOrderRequest {
  id: string;
  name: string;
  supplierId: number;
  mappingSystemVersion: number;
  selectedStyleVariants: PoStyleVariant[];
  selectedItemIds: number[];
  categorySelections?: number[];
  /** @deprecated Use processTypeSelections instead */
  selectedProcessTypeIds?: number[];
  /** Process selections for PO create (replaces selectedProcessTypeIds) */
  processTypeSelections?: ProcessTypeSelectionItem[];
  etd: string; // ISO 8601 date string
  supplierContactPersonId?: string;
}

export interface PurchaseOrderData {
  id: string;
  organizationId?: number;
  name?: string;
  supplierId?: number;
  mappingSystemVersion?: number;
  organization?: Organization;
  createdByUser?: CreatedByUser;
  supplier?: Organization;
  status?: PoState;
  stylesCount?: number;
  itemsCount?: number;
  createdAt?: string;
  publishedAt?: string;
  etd?: string;
  poObjectives?: PoObjective[];
  submitForApprovalAt?: string;
  approvedBy?: CreatedByUser;
  submitForApprovalByUser?: CreatedByUser;
  processConfirmedAt?: string;
  supplierContactPersonId?: string;
  supplierContactPerson?: CreatedByUser;
  stateBeforeOnHold?: number | null;
}

export interface PoObjective {
  id?: number;
  poID?: string;
  organizationID?: number;
  facilityProcessTypeID?: number;
  urgency?: number;
  facilityProcessType?: ProcessingOptionType;
}

export interface CreatedByUser {
  email?: string;
  id?: string;
  name?: string;
  avatarColor?: string;
}

export interface Organization {
  type?: string;
  status?: string;
  id?: number;
  name?: string;
}

export interface PoState {
  localeCode?: string;
  visibilityStatus?: string;
  poMappingRequestTab?: string;
  uiCapabilities?: string[];
  id?: number;
  name?: string;
}

export interface PoCountResponseDto {
  pendingCount: number;
  reviewAndApprovalCount: number;
  doneCount: number;
  awaitingResponseCount: number;
}

export interface PoMappingCountResponseDto {
  totalStyleCount: number;
  totalItemCount: number;
  styleTabCount: number;
  itemTabCount: number;
  totalCertificateMappingCount?: number;
  certificateMappingCompleted?: number;
  styleMappingPercentage?: number; // Only present for mapping count endpoint
  itemMappingPercentage?: number; // Only present for mapping count endpoint
  certificateMappingPercentage?: number; // Only present for mapping count endpoint
  // Review count specific fields (only present for review count endpoint)
  updatePoState?: boolean;
  poLocalCode?: string; // PO state LocaleCode (e.g., "NeedsReview", "Approved", "PartiallyApproved")
  warningFor?: CertificateWarningFor;
}

/** Certificate expiry warning shown on PO mapping review certificates tab. */
export type CertificateWarningFor = 'expiring_soon' | 'expired';

/**
 * PO Visibility Status values matching backend PoVisibilityStatus enum names
 * These are the values that the backend expects in the UpdatePo API
 * Backend uses Enum.TryParse to parse these values, so they must match the enum names exactly
 */
export enum PoVisibilityStatus {
  Unpublished = 'Unpublished',
  Published = 'Published',
  OnHold = 'OnHold',
  Cancelled = 'Cancelled',
  Initiated_mapping = 'Initiated_mapping',
  Submit_for_approval = 'Submit_for_approval',
  Approved = 'Approved',
  Partial_approved = 'Partial_approved',
  Needs_review = 'Needs_review',
}

export interface PoProcessType {
  id: number;
  name: string;
  type: number;
  subsupplierDetails: SubsupplierDetail[];
  facilityProcessType?: { id?: number; name?: string };
}

export type SubsupplierDetailUICapabilities =
  | 'supplier_can_view'
  | 'supplier_cannot_view'
  | 'mapping_enabled'
  | 'mapping_disabled'
  | 'document_upload_enabled'
  | 'document_upload_disabled'
  | 'changes_enabled'
  | 'changes_disabled';

export interface SubsupplierDetail {
  id: string;
  name: string;
  avatarColor: string;
  facilityDetails: FacilityDetail[];
  certificateDetails?: ProductCertificateDetail[];
  organizationSubSupplierId?: string;
  poVariantId?: number;
  organizationId?: number;
  value?: null;
  itemSettingId?: number;
  uiCapabilities?: SubsupplierDetailUICapabilities[];
  poFacilityProcessIds?: number[];
  styleColors?: StyleColor[];
  styleColorName?: string;
  styleColorId?: string;
  styleColorId2?: string;
  status?: string;
  approvedBy?: ApprovedBy;
  mappedBy?: string;
  isMappedByDesigner?: boolean;
  certificateWarningFor?: CertificateWarningFor;
}

export interface ApprovedBy {
  id: string;
  name: string;
}

export interface FacilityDetail {
  id: string;
  name: string;
  poFacilityProcessId?: number;
  /** Per-facility approval status from review API. */
  status?: string;
  approvedBy?: ApprovedBy;
  /** Variants mapped to this facility; null when none. */
  styleColors?: StyleColor[] | null;
  /** Product certificates from sub-suppliers API (`Certificates` → `certificates`). */
  certificates?: FacilityCertificate[];
  /** @deprecated Legacy API alias for `certificates`. */
  certificate?: FacilityCertificate[];
}

export interface ProductCertificateDetail {
  id: string;
  name: string;
  poStyleProductCertificateId?: number;
  certificateNumber?: string;
  standardName?: string;
  validFrom?: string;
  validTo?: string;
  status?: string;
  /** Mapped supplier facility id when assigned certificate comes from contents API. */
  facilityId?: string;
  documentUrl?: string;
}

/** Dropdown option built from PO sub-supplier facility certificates + mapped facility on styles/contents. */
export type StyleProductCertificateOption = {
  id: string;
  label: string;
  certificateNumber: string;
  standardName: string;
  instituteName: string;
  facilityId: string;
  facilityName: string;
  subsupplierId: string;
  facilityCertificateId: number;
  validFrom?: string;
  validTo?: string;
  status?: string;
  supplierStatus?: string;
  documentUrl?: string;
};

/**
 * Certificate shape returned by `GET .../styles/contents?type=certificates`.
 * Matches the backend `CertificateData` domain model.
 */
export type PoFacilityCertificate = {
  /**
   * `FacilityCertificate.Id` — identity of the certificate itself, stable across every process /
   * sub-supplier / facility / color variant it is attached to, and populated for facility as well
   * as product certificates. De-duplicate on this, not on `poFacilityProcessCertificateId`.
   *
   * Serialized as a JSON **number** by this endpoint (a SQL `BIGINT`), while the sub-suppliers APIs
   * send it as a string — so normalise with `String(...)` before any string operation on it.
   */
  id?: string | number;
  /** Assignment id (`POFacilityProcessCertificate.Id`); null for unassigned facility certificates. */
  poFacilityProcessCertificateId?: number;
  /** Variant process row this certificate is assigned to. */
  poFacilityProcessId?: number;
  /** "facility" | "product" */
  certificateType?: string;
  certificateNumber?: string;
  validFrom?: string;
  validTo?: string | null;
  expiryNotificationDate?: string | null;
  status?: string;
  addedAutomatically?: boolean;
  standard?: {
    id?: number;
    name?: string;
  };
  documentUrl?: string | null;
  uiCapabilities?: SubsupplierDetailUICapabilities[];
};

/** Facility row on PO mapping APIs when `styles/contents?type=facilities` is used. */
export type FacilityDetailWithCertificates = Omit<FacilityDetail, 'certificates'> & {
  certificates?: PoFacilityCertificate[];
};

export type SubsupplierDetailWithCertificates = Omit<SubsupplierDetail, 'facilityDetails'> & {
  facilityDetails: FacilityDetailWithCertificates[];
};

export type PoProcessTypeWithCertificates = Omit<PoProcessType, 'subsupplierDetails'> & {
  subsupplierDetails: SubsupplierDetailWithCertificates[];
};

/** Style row from `GET .../styles/contents?type=certificates`. */
export type StyleCertificateReviewData = Omit<
  StyleData,
  'subsupplierFacilityDetailsList' | 'childs'
> & {
  subsupplierFacilityDetailsList?: PoProcessTypeWithCertificates[];
  childs?: StyleCertificateReviewData[];
};

export interface StylesVariant extends StylesForDetails {
  color?: string;
  colorId?: string;
}
export interface StylesForDetailsItem {
  name?: string;
  number?: string;
  id?: number;
  itemCategories?: string;
  supplier?: { id: string; name: string };
  itemLogo?: string;
}

export interface StylesForDetails {
  id: string;
  project?: null | string;
  description?: string;
  organization?: {
    name?: string;
  };
  styleName: string;
  styleNumber: string;
  brand: DefaultDataWithLogo;
  supplier: DefaultData;
  season: DefaultData;
  group: DefaultData;
  state: number;
  categories: string;
  styleLogo: string;
  styleColors: DefaultData[];
  items: StylesForDetailsItem[];
  subSupplierFacilityDetails?: SubSupplierFacilityDetail[];
  stylesVariants: StylesVariant[];
  customFields: CustomField[];
}

export interface DefaultData {
  id: string;
  name: string;
}
export interface DefaultDataWithLogo extends DefaultData {
  logoUrl: string;
}
export interface SubSupplierFacilityDetail {
  id: string;
  name: string;
  subsupplierDetails: SubsupplierDetail[];
  type?: number;
}

export interface LinkedStylesItemsForDetails
  extends Omit<StylesForDetails, 'items' | 'subSupplierFacilityDetails' | 'stylesVariants'> {
  styleColorName?: string;
}

export interface ItemsForDetails extends Omit<ItemData, 'mappingStatus' | 'linkedStyles'> {
  supplier: Omit<SubsupplierDetail, 'facilityDetails'>;
  //suppliedBy: Omit<SubsupplierDetail, 'facilityDetails'>;
  linkedStyles?: LinkedStylesItemsForDetails[];
}

export interface PoFacilityProcessesData extends Omit<SubsupplierDetail, 'facilityDetails'> {
  facilityProcessTypeId?: number;
  facilityProcessTypeName?: string;
  facilityDetails: FacilityDetailWithCertificates[];
}

/**
 * Union of FacilityCertificate (sub-suppliers / review-approve flow)
 * and PoFacilityCertificate (styles/contents?type=certificates flow).
 * Use this in shared components that render certificates from either API.
 */
export type AnyCertificate = FacilityCertificate | PoFacilityCertificate;

export interface StyleDataForReview extends Omit<StyleData, 'state'> {
  poFacilityProcesses?: (PoProcessType & {
    facilityProcessType?: { id?: number; name?: string };
  })[];
  status?: string;
  parentApprovalStatus?: string;
  poFacilityProcessesData?: PoFacilityProcessesData[];
}

export interface ItemDataForReview extends ItemData {
  poFacilityProcesses?: PoProcessType[];
  status?: string;
  parentApprovalStatus?: string;
  poFacilityProcessesData?: PoFacilityProcessesData[];
}
export interface SubsupplierDetails {
  id: string;
  name: string;
  avatarColor: string;
  parentOrganizationType: number;
}
export interface SupplierApprovalItem {
  id: number;
  name: string;
  type: number;
  approvalStatus: string;
  mappingStatus: string;
  subsupplierDetails: SubsupplierDetails[];
}

export type SupplierApprovalData = SupplierApprovalItem[];

export interface ProcessCategory {
  id: number;
  name?: string;
  localeCode?: string;
  description?: string;
  processes?: ProcessCategoryProcess[];
}

/** Process item within GET /api/process-categories */
export type ProcessCategoryProcess = {
  id: number;
  name: string;
  type: number;
};
export type ProcessObjectiveCategoryUiCapabilities = 'is_requested_by_brand' | 'is_mandatory';
/** Row from GET /api/.../process-objectives */
export type ProcessObjective = {
  /** PO objective row id (for PUT process-selections delete/update). Same as `poObjectiveId` if duplicated. */
  id?: number;
  poObjectiveId?: number;
  poId?: string;
  /** Facility / process type id (matches process-categories process `id`). */
  facilityProcessTypeId?: number;
  uiCapabilities?: ProcessObjectiveCategoryUiCapabilities[];
  facilityProcessCategoryId?: number;
};

/** Item in PUT process-selections response */
export type ProcessSelectionResponseItem = {
  id: number;
  poId: string;
  facilityProcessTypeId: number;
  isRequestedByBrand: boolean;
  isMandatory: boolean;
};
